import { useState, useEffect, Suspense, lazy } from 'react';
const CreateGroup = lazy(() => import('./components/CreateGroup').then(m => ({ default: m.CreateGroup })));
const JoinGroup = lazy(() => import('./components/JoinGroup').then(m => ({ default: m.JoinGroup })));
const GroupDashboard = lazy(() => import('./components/GroupDashboard').then(m => ({ default: m.GroupDashboard })));
import { Toaster } from './components/ui/sonner';
import { AuthWrapper } from './components/Auth/AuthWrapper';
import { UserMenu } from './components/Auth/UserMenu';
import { OfflineIndicator } from './components/OfflineIndicator';
import { PWAUtils } from './components/PWAUtils';
import { UserGroupsCarousel } from './components/UserGroupsCarousel';
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicy })));
const TermsOfUse = lazy(() => import('./components/TermsOfUse').then(m => ({ default: m.TermsOfUse })));
const AccountDeletion = lazy(() => import('./components/AccountDeletion').then(m => ({ default: m.AccountDeletion })));
const ContactPage = lazy(() => import('./components/ContactPage').then(m => ({ default: m.ContactPage })));
import { LanguageSwitcher } from './components/LanguageSwitcher';
import { SEOHead } from './components/SEOHead';
import { PublicMenu } from './components/PublicMenu';
import { Blog } from './components/Blog';
import { BlogPost1 } from './components/BlogPost1';
import { BlogPost2 } from './components/BlogPost2';
import { BlogPost3 } from './components/BlogPost3';
import { BlogPost4 } from './components/BlogPost4';
import { BlogPost5 } from './components/BlogPost5';
import { BlogPost6 } from './components/BlogPost6';
const MessagesFeature = lazy(() => import('./components/MessagesFeature').then(m => ({ default: m.MessagesFeature })));
const WishlistFeature = lazy(() => import('./components/WishlistFeature').then(m => ({ default: m.WishlistFeature })));
const ThemesFeature = lazy(() => import('./components/ThemesFeature').then(m => ({ default: m.ThemesFeature })));
const AIFeature = lazy(() => import('./components/AIFeature').then(m => ({ default: m.AIFeature })));
const DrawFeature = lazy(() => import('./components/DrawFeature').then(m => ({ default: m.DrawFeature })));
const FAQPage = lazy(() => import('./components/FAQPage').then(m => ({ default: m.FAQPage })));
const AboutPage = lazy(() => import('./components/AboutPage').then(m => ({ default: m.AboutPage })));
import { supabase } from './utils/supabase/client';
import { FeaturesShowcase } from './components/FeaturesShowcase';
import { LatestPosts } from './components/LatestPosts';
import { Footer } from './components/Footer';
import { Gift, Sparkles, LogOut, Home, MessageCircle, Users, UserPlus, Dice1, Palette, Clock, Bot } from 'lucide-react';
import { languages } from './utils/i18n';
import { Button } from './components/ui/button';
import { toast } from 'sonner';
import { User } from '@supabase/supabase-js';
import { useTranslation } from './utils/useTranslation';

import { useDeepLink } from './utils/use-deep-link';

export type AppView = 'home' | 'create' | 'join' | 'dashboard' | 'privacy' | 'terms' | 'contact' | 'about' | 'account-deletion' | 'feature-messages' | 'feature-wishlist' | 'feature-themes' | 'feature-ai' | 'feature-draw' | 'faq' | 'blog' | 'blog-post-1' | 'blog-post-2' | 'blog-post-3' | 'blog-post-4' | 'blog-post-5' | 'blog-post-6';

export default function App() {
  useDeepLink(); // Ativa o listener de Deep Links
  const { t, tVar, currentLang } = useTranslation(); // Hook de tradução

  const getMascotImage = (lang: string) => {
    const mascots: Record<string, string> = {
      'pt-BR': '/Brasil.webp',
      'en-US': '/Estados Unidos.webp',
      'es-ES': '/Espanha.webp',
      'de-DE': '/Alemanha.webp',
      'fr-FR': '/França.webp',
      'it-IT': '/Italia.webp',
      'zh-CN': '/china.webp',
      'ko-KR': '/Coreia_do_Sul.webp',
      'ja-JP': '/Japão.webp',
    };
    return mascots[lang] || '/Mascote.webp';
  };

  const mascotImage = getMascotImage(currentLang);

  const [view, setView] = useState<AppView>('home');
  const [groupId, setGroupId] = useState<string | null>(null);
  const [participantId, setParticipantId] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [initialTab, setInitialTab] = useState<string | undefined>(undefined);

  useEffect(() => {
    const path = window.location.pathname.replace(/\/+$/, '');
    const segments = path.split('/').filter(Boolean);
    const shortLangs = Object.values(languages).map(l => l.short);
    const isLangPrefixed = segments.length > 0 && (shortLangs as string[]).includes(segments[0]);
    const seg = isLangPrefixed ? (segments[1] || '') : (segments[0] || '');
    const seg2 = isLangPrefixed ? (segments[2] || '') : (segments[1] || '');

    if (seg === 'create') setView('create');
    else if (seg === 'join') setView('join');
    else if (seg === 'privacy') setView('privacy');
    else if (seg === 'terms') setView('terms');
    else if (seg === 'contact') setView('contact');
    else if (seg === 'about') setView('about');
    else if (seg === 'faq') setView('faq');
    else if (seg === 'blog') setView('blog');
    else if (seg === 'account-deletion') setView('account-deletion');
    else if (seg === 'features' && seg2 === 'messages') setView('feature-messages');
    else if (seg === 'features' && seg2 === 'wishlist') setView('feature-wishlist');
    else if (seg === 'features' && seg2 === 'themes') setView('feature-themes');
    else if (seg === 'features' && seg2 === 'ai') setView('feature-ai');
    else if (seg === 'features' && seg2 === 'draw') setView('feature-draw');
    else setView('home');
  }, []);

  // Update URL when view changes
  useEffect(() => {
    let path = '/';
    if (view === 'create') path = '/create';
    else if (view === 'join') path = '/join';
    else if (view === 'dashboard') path = '/dashboard';
    else if (view === 'privacy') path = '/privacy';
    else if (view === 'terms') path = '/terms';
    else if (view === 'contact') path = '/contact';
    else if (view === 'about') path = '/about';
    else if (view === 'faq') path = '/faq';
    else if (view === 'blog') path = '/blog';
    else if (view === 'account-deletion') path = '/account-deletion';
    else if (view === 'feature-messages') path = '/features/messages';
    else if (view === 'feature-wishlist') path = '/features/wishlist';
    else if (view === 'feature-themes') path = '/features/themes';
    else if (view === 'feature-ai') path = '/features/ai';
    else if (view === 'feature-draw') path = '/features/draw';
    else if (view.startsWith('blog-post-')) path = `/blog/${view.replace('blog-post-', '')}`;

    const currentPath = window.location.pathname;
    // Only push if path is different to avoid loops/redundancy
    // Also handle language prefix if present
    const segments = currentPath.split('/').filter(Boolean);
    const supportedLangs = ['pt', 'en', 'es', 'fr', 'de', 'it', 'zh', 'ko', 'ja'];
    const hasLangPrefix = supportedLangs.includes(segments[0]);

    const targetPath = hasLangPrefix ? `/${segments[0]}${path === '/' ? '' : path}` : path;

    if (currentPath !== targetPath) {
      window.history.pushState({}, '', targetPath);
    }
  }, [view]);

  // Check for existing session and auth state
  useEffect(() => {
    // Check authentication state
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setUser(session?.user ?? null);

      // Se o usuário está logado, busca o último grupo que ele participou
      if (session?.user?.email) {
        try {
          const { data: participants, error } = await supabase
            .from('participants')
            .select('id, group_id, groups!inner(id, name)')
            .eq('email', session.user.email.toLowerCase())
            .order('created_at', { ascending: false })
            .limit(1);

          if (!error && participants && participants.length > 0) {
            const lastParticipant = participants[0];
            setGroupId(lastParticipant.group_id);
            setParticipantId(lastParticipant.id);
            localStorage.setItem('amigoSecretoGroupId', lastParticipant.group_id);
            localStorage.setItem('amigoSecretoParticipantId', lastParticipant.id);
            setView('dashboard');
          }
        } catch (error) {
          console.error('Error fetching user groups:', error);
        }
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    // Listen for deep link navigation events
    const handleDeepLinkNav = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.groupId) {
        console.log('📱 Navigating to join group from deep link');
        setView('join');
      }
    };

    window.addEventListener('navigate-to-join', handleDeepLinkNav);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('navigate-to-join', handleDeepLinkNav);
    };
  }, []);

  const handleGroupCreated = (newGroupId: string, newParticipantId: string) => {
    setGroupId(newGroupId);
    setParticipantId(newParticipantId);
    localStorage.setItem('amigoSecretoGroupId', newGroupId);
    localStorage.setItem('amigoSecretoParticipantId', newParticipantId);
    setInitialTab('participants'); // Direciona para aba de compartilhamento
    setView('dashboard');
  };

  const handleGroupJoined = (joinedGroupId: string, joinedParticipantId: string) => {
    setGroupId(joinedGroupId);
    setParticipantId(joinedParticipantId);
    localStorage.setItem('amigoSecretoGroupId', joinedGroupId);
    localStorage.setItem('amigoSecretoParticipantId', joinedParticipantId);
    setView('dashboard');
  };

  const handleLeaveGroup = () => {
    // NÃO limpa localStorage - permite voltar ao grupo ao reabrir o app
    // Apenas muda a view para home
    setView('home');
  };

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      localStorage.removeItem('amigoSecretoGroupId');
      localStorage.removeItem('amigoSecretoParticipantId');
      setGroupId(null);
      setParticipantId(null);
      setView('home');
      setUser(null);

      toast.success(t('auth.logoutSuccess'));
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : t('auth.logoutError');
      toast.error(errorMessage);
    }
  };

  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [loginAction, setLoginAction] = useState<'create' | 'join' | null>(null);
  const [requireAuth, setRequireAuth] = useState(false);

  const handleCreateGroupClick = () => {
    if (!user) {
      setLoginAction('create');
      setShowLoginPrompt(true);
      setRequireAuth(false);
    } else {
      setView('create');
    }
  };

  const handleJoinGroupClick = () => {
    if (!user) {
      setLoginAction('join');
      setRequireAuth(true);
      setShowLoginPrompt(false);
    } else {
      setView('join');
    }
  };

  const handleDemoRequest = () => {
    if (user) {
      if (groupId) {
        setView('dashboard');
      } else {
        setView('create');
      }
    } else {
      setLoginAction('create');
      setShowLoginPrompt(true);
      setRequireAuth(false);
    }
  };

  const handleLoginSuccess = () => {
    setRequireAuth(false);
    setShowLoginPrompt(false);
    if (loginAction === 'create') {
      setView('create');
    } else if (loginAction === 'join') {
      setView('join');
    }
    setLoginAction(null);
  };

  return (
    <AuthWrapper
      requireAuth={requireAuth}
      onAuthSuccess={handleLoginSuccess}
    >
      <SEOHead />
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-green-50">
        <Toaster />
        <OfflineIndicator />

        {/* Login Prompt Modal */}
        {showLoginPrompt && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
            <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
              <h2 className="text-2xl font-bold mb-4 text-center">{t('auth.loginRequired')}</h2>
              <p className="text-gray-600 mb-6 text-center">
                {tVar('auth.loginMessage', {
                  action: loginAction === 'create' ? t('auth.create') : t('auth.join')
                })}
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-800">
                  <strong>{t('auth.whyLogin')}</strong><br />
                  • {t('auth.reason1')}<br />
                  • {t('auth.reason2')}<br />
                  • {t('auth.reason3')}<br />
                  • {t('auth.reason4')}
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowLoginPrompt(false);
                    setLoginAction(null);
                  }}
                  className="flex-1"
                >
                  {t('auth.cancel')}
                </Button>
                <Button
                  onClick={() => {
                    setRequireAuth(true);
                    setShowLoginPrompt(false);
                  }}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                >
                  {t('auth.doLogin')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* User Menu or Login Button */}
        {view !== 'dashboard' && (
          <div className="fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-xl border-b border-white/20 shadow-lg">
            <div className="container mx-auto px-4 py-3">
              <div className="flex items-center justify-between">
                {/* Logo/Brand */}
                <div className="flex items-center gap-2">
                  <img
                    src={mascotImage}
                    alt="Mystery Gift"
                    className="w-10 h-10 object-contain"
                  />
                  <span className="text-xl font-bold bg-gradient-to-r from-red-600 to-green-600 bg-clip-text text-transparent hidden sm:inline">
                    Mystery Gift
                  </span>
                </div>

                {/* Public Menu - Visible on all public pages */}
                {view !== 'create' && view !== 'join' && (
                  <div className="flex-1 flex justify-center">
                    <PublicMenu onNavigate={(newView) => setView(newView)} />
                  </div>
                )}

                <div className={`flex items-center gap-2 ${view === 'home' ? '' : 'ml-auto'}`}>
                  <LanguageSwitcher />
                  {user ? (
                    <>
                      <UserMenu user={user} />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleLogout}
                        className="text-red-600 hover:text-red-700"
                      >
                        <LogOut className="h-4 w-4" />
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={() => { setRequireAuth(true); setShowLoginPrompt(false); setLoginAction(null); }}
                      className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                    >
                      {t('nav.login')}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}


        {view === 'home' && (
          <>
            {/* Hero Banner */}
            <div className="relative overflow-hidden bg-gradient-to-br from-purple-600 via-pink-500 to-red-500 pt-24 pb-16">
              {/* Decorative circles */}
              <div className="absolute top-10 right-10 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
              <div className="absolute bottom-10 left-10 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>

              <div className="container mx-auto px-4 max-w-6xl relative z-10">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  {/* Left Side - Content */}
                  <div className="text-white">
                    <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-md rounded-full text-sm font-medium mb-6 border border-white/30">
                      ✨ AMIGO SECRETO INTELIGENTE
                    </div>

                    <h1 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight drop-shadow-lg">
                      {t('home.title')}
                    </h1>

                    <p className="text-xl md:text-2xl mb-4 text-white/90 leading-relaxed">
                      {t('home.subtitle')}
                    </p>

                    <div className="flex items-center gap-2 text-white/80 mb-8">
                      <Sparkles className="w-5 h-5" />
                      <span className="text-lg">{t('home.taglineExtra')}</span>
                    </div>

                    {/* CTA Buttons */}
                    <div className="flex flex-wrap gap-4">
                      <button
                        onClick={handleCreateGroupClick}
                        className="px-8 py-4 bg-white text-purple-600 rounded-full font-bold text-lg hover:bg-gray-100 hover:scale-105 transition-all shadow-xl flex items-center gap-2"
                      >
                        <Gift className="w-5 h-5" />
                        {t('home.createGroup')}
                      </button>
                      <button
                        onClick={handleJoinGroupClick}
                        className="px-8 py-4 bg-white/20 backdrop-blur-md text-white border-2 border-white rounded-full font-bold text-lg hover:bg-white/30 transition-all flex items-center gap-2"
                      >
                        <Users className="w-5 h-5" />
                        {t('home.joinGroup')}
                      </button>
                    </div>
                  </div>

                  {/* Right Side - Mascot */}
                  <div className="flex justify-center md:justify-end">
                    <div className="relative">
                      {/* Glow effect */}
                      <div className="absolute inset-0 bg-white/20 rounded-full blur-3xl scale-110"></div>
                      <img
                        src={mascotImage}
                        alt="Mascote Mystery Gift"
                        className="relative w-64 h-64 md:w-96 md:h-96 object-contain drop-shadow-2xl animate-float"
                        fetchPriority="high"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="container mx-auto px-4 pb-24 pt-12 max-w-4xl">
              {/* Carrossel de Grupos do Usuário */}
              <UserGroupsCarousel
                user={user}
                onGroupClick={(gId, pId) => {
                  setGroupId(gId);
                  setParticipantId(pId);
                  localStorage.setItem('amigoSecretoGroupId', gId);
                  localStorage.setItem('amigoSecretoParticipantId', pId);
                  setView('dashboard');
                }}
              />

              {/* PWA Utils - Apenas botão de instalar */}
              <div className="mb-8 flex justify-center">
                <PWAUtils showInstallButton />
              </div>

              <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
                <button
                  onClick={handleCreateGroupClick}
                  className="group relative overflow-hidden bg-gradient-to-br from-purple-600 to-purple-700 text-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all hover:scale-105"
                >
                  <div className="relative z-10 flex flex-row items-center justify-center md:justify-start text-left gap-4">
                    <img src="/home-icons/Criar%20Grupo.svg" alt="Criar Grupo" className="flex-shrink-0 w-36 h-36 md:w-42 md:h-42 object-contain" fetchPriority="high" />
                    <div className="flex-1 min-w-0">
                      <h2 className="text-2xl mb-2">{t('home.createGroup')}</h2>
                      <p className="text-purple-100">
                        {t('home.organize')}
                      </p>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>

                <button
                  onClick={handleJoinGroupClick}
                  className="group relative overflow-hidden text-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all hover:scale-105"
                  style={{ background: 'linear-gradient(to bottom right, #FF5F6D, #FE4A5B)' }}
                >
                  <div className="relative z-10 flex flex-row items-center justify-center md:justify-start text-left gap-4">
                    <img src="/home-icons/Entrar%20no%20grupo.svg" alt="Entrar no Grupo" className="flex-shrink-0 w-36 h-36 md:w-42 md:h-42 object-contain" fetchPriority="high" />
                    <div className="flex-1 min-w-0">
                      <h2 className="text-2xl mb-2">{t('home.joinGroup')}</h2>
                      <p className="text-red-50">
                        {t('home.participate')}
                      </p>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              </div>

            </div>

            {/* Features Showcase */}
            <FeaturesShowcase
              onNavigate={(view) => setView(view as AppView)}
              onDemoRequest={handleDemoRequest}
              onCreateGroup={handleCreateGroupClick}
            />

            {/* Latest Posts */}
            <LatestPosts onNavigate={(view) => setView(view)} />

            {/* Footer Completo */}
            <Footer onNavigate={(view) => setView(view)} />
          </>
        )}

        {/* Bottom Navigation (Mobile Only) */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-2 shadow-lg z-50">
          <div className="flex justify-around items-center max-w-md mx-auto">
            <button
              className="flex flex-col items-center p-2 rounded-lg transition-colors text-purple-600"
            >
              <Home className="size-6 mb-1" />
              <span className="text-xs">{t('nav.home')}</span>
            </button>

            <button
              onClick={() => toast.error(t('toasts.requireGroup.wishlist'))}
              className="flex flex-col items-center p-2 rounded-lg transition-colors text-gray-500"
            >
              <Gift className="size-6 mb-1" />
              <span className="text-xs">{t('nav.wishes')}</span>
            </button>

            <button
              onClick={() => toast.error(t('toasts.requireGroup.messages'))}
              className="flex flex-col items-center p-2 rounded-lg transition-colors text-gray-500"
            >
              <MessageCircle className="size-6 mb-1" />
              <span className="text-xs">{t('nav.messages')}</span>
            </button>

            <button
              onClick={() => toast.error(t('toasts.requireGroup.participants'))}
              className="flex flex-col items-center p-2 rounded-lg transition-colors text-gray-500"
            >
              <Users className="size-6 mb-1" />
              <span className="text-xs">{t('nav.group')}</span>
            </button>
          </div>
        </div>



        {
          view === 'create' && (
            <Suspense fallback={<div className="p-8 text-center">{t('common.loading')}</div>}>
              <CreateGroup
                onGroupCreated={handleGroupCreated}
                onBack={() => setView('home')}
                user={user}
              />
            </Suspense>
          )
        }

        {
          view === 'join' && (
            <Suspense fallback={<div className="p-8 text-center">{t('common.loading')}</div>}>
              <JoinGroup
                onGroupJoined={handleGroupJoined}
                onBack={() => setView('home')}
                user={user}
              />
            </Suspense>
          )
        }

        {
          view === 'dashboard' && groupId && participantId && (
            <Suspense fallback={<div className="p-8 text-center">{t('common.loading')}</div>}>
              <GroupDashboard
                groupId={groupId}
                participantId={participantId}
                onLeave={handleLeaveGroup}
                user={user}
                onLogout={handleLogout}
                initialTab={initialTab}
              />
            </Suspense>
          )
        }

        {
          view === 'privacy' && (
            <>
              <Suspense fallback={<div className="p-8 text-center">{t('common.loading')}</div>}>
                <PrivacyPolicy onBack={() => setView('home')} />
              </Suspense>
              <Footer onNavigate={(view) => setView(view)} />
            </>
          )
        }
        {
          view === 'terms' && (
            <>
              <Suspense fallback={<div className="p-8 text-center">{t('common.loading')}</div>}>
                <TermsOfUse onBack={() => setView('home')} />
              </Suspense>
              <Footer onNavigate={(view) => setView(view)} />
            </>
          )
        }
        {
          view === 'contact' && (
            <>
              <Suspense fallback={<div className="p-8 text-center">{t('common.loading')}</div>}>
                <ContactPage onBack={() => setView('home')} />
              </Suspense>
              <Footer onNavigate={(view) => setView(view)} />
            </>
          )
        }
        {
          view === 'about' && (
            <>
              <Suspense fallback={<div className="p-8 text-center">{t('common.loading')}</div>}>
                <AboutPage onBack={() => setView('home')} />
              </Suspense>
              <Footer onNavigate={(view) => setView(view)} />
            </>
          )
        }
        {
          view === 'faq' && (
            <>
              <Suspense fallback={<div className="p-8 text-center">{t('common.loading')}</div>}>
                <FAQPage />
              </Suspense>
              <Footer onNavigate={(view) => setView(view)} />
            </>
          )
        }
        {
          view === 'account-deletion' && (
            <>
              <Suspense fallback={<div className="p-8 text-center">{t('common.loading')}</div>}>
                <AccountDeletion onBack={() => setView('home')} />
              </Suspense>
              <Footer onNavigate={(view) => setView(view)} />
            </>
          )
        }

        {view === 'blog' && (
          <>
            <Suspense fallback={<div className="p-8 text-center">{t('common.loading')}</div>}>
              <Blog onBack={() => setView('home')} onNavigate={(view) => setView(view)} />
            </Suspense>
            <Footer onNavigate={(view) => setView(view)} />
          </>
        )}
        {
          view === 'blog-post-1' && (
            <>
              <Suspense fallback={<div className="p-8 text-center">{t('common.loading')}</div>}>
                <BlogPost1 onBack={() => setView('blog')} />
              </Suspense>
              <Footer onNavigate={(view) => setView(view)} />
            </>
          )
        }
        {
          view === 'blog-post-2' && (
            <>
              <Suspense fallback={<div className="p-8 text-center">{t('common.loading')}</div>}>
                <BlogPost2 onBack={() => setView('blog')} />
              </Suspense>
              <Footer onNavigate={(view) => setView(view)} />
            </>
          )
        }
        {
          view === 'blog-post-3' && (
            <>
              <Suspense fallback={<div className="p-8 text-center">{t('common.loading')}</div>}>
                <BlogPost3 onBack={() => setView('blog')} />
              </Suspense>
              <Footer onNavigate={(view) => setView(view)} />
            </>
          )
        }
        {
          view === 'blog-post-4' && (
            <>
              <Suspense fallback={<div className="p-8 text-center">{t('common.loading')}</div>}>
                <BlogPost4 onBack={() => setView('blog')} />
              </Suspense>
              <Footer onNavigate={(view) => setView(view)} />
            </>
          )
        }
        {
          view === 'blog-post-5' && (
            <>
              <Suspense fallback={<div className="p-8 text-center">{t('common.loading')}</div>}>
                <BlogPost5 onBack={() => setView('blog')} />
              </Suspense>
              <Footer onNavigate={(view) => setView(view)} />
            </>
          )
        }
        {
          view === 'blog-post-6' && (
            <>
              <Suspense fallback={<div className="p-8 text-center">{t('common.loading')}</div>}>
                <BlogPost6 onBack={() => setView('blog')} />
              </Suspense>
              <Footer onNavigate={(view) => setView(view)} />
            </>
          )
        }

        {
          view === 'feature-messages' && (
            <>
              <Suspense fallback={<div className="p-8 text-center">{t('common.loading')}</div>}>
                <MessagesFeature />
              </Suspense>
              <Footer onNavigate={(view) => setView(view)} />
            </>
          )
        }
        {
          view === 'feature-wishlist' && (
            <>
              <Suspense fallback={<div className="p-8 text-center">{t('common.loading')}</div>}>
                <WishlistFeature />
              </Suspense>
              <Footer onNavigate={(view) => setView(view)} />
            </>
          )
        }
        {
          view === 'feature-themes' && (
            <>
              <Suspense fallback={<div className="p-8 text-center">{t('common.loading')}</div>}>
                <ThemesFeature />
              </Suspense>
              <Footer onNavigate={(view) => setView(view)} />
            </>
          )
        }
        {
          view === 'feature-ai' && (
            <>
              <Suspense fallback={<div className="p-8 text-center">{t('common.loading')}</div>}>
                <AIFeature />
              </Suspense>
              <Footer onNavigate={(view) => setView(view)} />
            </>
          )
        }
        {
          view === 'feature-draw' && (
            <>
              <Suspense fallback={<div className="p-8 text-center">{t('common.loading')}</div>}>
                <DrawFeature onCreateGroup={handleCreateGroupClick} />
              </Suspense>
              <Footer onNavigate={(view) => setView(view)} />
            </>
          )
        }
      </div>
    </AuthWrapper>
  );
}

